import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["unifiedLayout", "ornamentLayout", "heartOrnamentLayout", "lastUpdated"]
  static values = {
    autoRefresh: Boolean,
    darkMode: Boolean,
    debug: Boolean
  }

  connect() {
    console.log("Files controller connected")
    this.checkSystemTheme()
    this.startAutoRefresh()
  }

  disconnect() {
    this.stopAutoRefresh()
  }

  checkSystemTheme() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      this.darkModeValue = true
      document.body.classList.add('dark-mode')
    }
  }

  toggleTheme() {
    this.darkModeValue = !this.darkModeValue
    document.body.classList.toggle('dark-mode', this.darkModeValue)
  }

  toggleAutoRefresh() {
    this.autoRefreshValue = !this.autoRefreshValue
    if (this.autoRefreshValue) {
      this.startAutoRefresh()
    } else {
      this.stopAutoRefresh()
    }
  }

  startAutoRefresh() {
    if (this.autoRefreshValue) {
      this.refreshInterval = setInterval(() => {
        this.refresh()
      }, 30000) // Refresh every 30 seconds
    }
  }

  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
  }

  async refresh() {
    try {
      const response = await fetch('/files', {
        headers: { 
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })
      
      if (response.ok) {
        const data = await response.json()
        
        // Update file counts in tab badges
        const unifiedBadge = document.getElementById('unified-count')
        const ornamentBadge = document.getElementById('ornament-count')
        const heartBadge = document.getElementById('heart-count')
        
        if (unifiedBadge) unifiedBadge.textContent = data.unified_layout_files.length
        if (ornamentBadge) ornamentBadge.textContent = data.ornament_files.length
        if (heartBadge) heartBadge.textContent = data.heart_ornament_files.length
        
        // Update the last updated timestamp
        this.lastUpdatedTarget.textContent = new Date().toLocaleString()
        
        // For now, we're just updating counts. Full UI refresh would require
        // rebuilding the file grids, which would be more complex
        console.log('Files refreshed successfully')
      } else {
        console.error('Failed to refresh files:', response.status)
      }
    } catch (error) {
      console.error('Error refreshing files:', error)
    }
  }

  forceUnifiedLayout(event) {
    event.preventDefault()
    console.log("Force Unified Layout clicked", event.currentTarget)
    this.post('/files/force_unified_layout')
  }

  forceOrnamentLayout(event) {
    event.preventDefault()
    console.log("Force Ornament Layout clicked", event.currentTarget)
    this.post('/files/force_ornament_layout')
  }

  forceHeartOrnamentLayout(event) {
    event.preventDefault()
    console.log("Force Heart Ornament Layout clicked", event.currentTarget)
    this.post('/files/force_heart_ornament_layout')
  }

  async post(url) {
    try {
      console.log("Starting POST request to:", url)
      const token = document.querySelector('meta[name="csrf-token"]')
      if (!token) {
        console.error("CSRF token meta tag not found")
        this.showFlash('CSRF token not found', 'error')
        return
      }
      console.log("Found CSRF token:", token.content)
      const fullUrl = url.startsWith('/') ? url : '/' + url
      console.log("Making fetch request to:", fullUrl)
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': token.content,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      })
      const data = await response.json()
      if (response.ok) {
        this.showFlash(data.message, 'success')
        this.refresh()
      } else {
        console.error('Server error:', data)
        this.showFlash(data.error || 'An error occurred', 'error')
      }
    } catch (error) {
      console.error('Network or parsing error:', error)
      this.showFlash('A network error occurred', 'error')
    }
  }

  showFlash(message, type = 'success') {
    const flash = document.createElement('div')
    flash.className = `flash-message ${type}`
    flash.textContent = message
    document.body.appendChild(flash)

    setTimeout(() => {
      flash.classList.add('fade-out')
      setTimeout(() => flash.remove(), 300)
    }, 14700)
  }
}
